import { useEffect, useCallback, useRef } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import { useProxyStore } from '../store/proxy-store';
import { useHarPresenceStore } from '../store/harPresenceStore';
import { useToolHarGrpcDecodeStore } from '../store/toolHarGrpcDecodeStore';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import {
  TOOL_EXECUTION_HAR_SSE_EVENT,
  type ToolHarSsePayload,
} from '@terra/shared-contracts-tracker';
import { BINARY_BODY_PREFIX, HAR_BASE64_COMMENT } from '@terra/shared-utils';
import { isBinaryGrpcWebOrConnectContentType } from '@terra/shared-asset-processing';

type ToolHarSSEEvent = ToolHarSsePayload;
type HarHeader = ToolHarSSEEvent['harEntry']['request']['headers'][number];

function harHeadersToRecord(headers: HarHeader[]): string {
  const record: Record<string, string | string[]> = {};

  for (const header of headers) {
    const existingValue = record[header.name];

    if (!existingValue) {
      record[header.name] = header.value;
      continue;
    }

    if (Array.isArray(existingValue)) {
      record[header.name] = [...existingValue, header.value];
      continue;
    }

    record[header.name] = [existingValue, header.value];
  }

  return JSON.stringify(record);
}

function harHeadersToRawHeaders(headers: HarHeader[]): string {
  const rawHeaders = headers.flatMap((header) => [header.name, header.value]);

  return JSON.stringify(rawHeaders);
}

function getUtf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

function normalizeHarBodySize({
  primarySize,
  fallbackText,
  secondarySize,
}: {
  primarySize: number | undefined;
  fallbackText: string | undefined;
  secondarySize?: number;
}): number {
  if (typeof primarySize === 'number' && primarySize >= 0) {
    return primarySize;
  }

  if (typeof secondarySize === 'number' && secondarySize >= 0) {
    return secondarySize;
  }

  if (fallbackText) {
    return getUtf8ByteLength(fallbackText);
  }

  return 0;
}

function getHostFromUrl(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

function getPathFromUrl(url: string): string {
  try {
    const parsed = new URL(url);

    return parsed.pathname + parsed.search;
  } catch {
    return '';
  }
}

function normalizeToolHarBodyForHistory({
  text,
  encoding,
}: {
  text: string | null | undefined;
  encoding?: string;
}): string | undefined {
  if (text == null || text === '') {
    return undefined;
  }

  if (text.startsWith(BINARY_BODY_PREFIX)) {
    return text;
  }

  if (encoding === 'base64') {
    return `${BINARY_BODY_PREFIX}${text}`;
  }

  return text;
}

function resolveHistoryBodyContentType({
  bodyText,
  encoding,
  mimeType,
}: {
  bodyText: string | undefined;
  encoding?: string;
  mimeType?: string;
}): 'text' | 'binary' | undefined {
  if (!bodyText) {
    return undefined;
  }

  if (
    encoding === 'base64' ||
    bodyText.startsWith(BINARY_BODY_PREFIX) ||
    (mimeType && isBinaryGrpcWebOrConnectContentType(mimeType))
  ) {
    return 'binary';
  }

  return 'text';
}

function getContentType(headers: HarHeader[]): string | undefined {
  const header = headers.find((h) => h.name.toLowerCase() === 'content-type');

  return header?.value;
}

function ensureContentTypeHeader({
  headers,
  contentType,
}: {
  headers: HarHeader[];
  contentType: string | undefined;
}): HarHeader[] {
  if (!contentType) return headers;
  const hasContentType = headers.some(
    (header) => header.name.toLowerCase() === 'content-type',
  );

  if (hasContentType) return headers;

  return [...headers, { name: 'Content-Type', value: contentType }];
}

type UseToolHarSSEParams = {
  targetId: string;
  targetType: TargetTypeType;
  enabled?: boolean;
};

export function useToolHarSSE({
  targetId,
  targetType,
  enabled = true,
}: UseToolHarSSEParams) {
  const { subscribe } = useSSE();
  const markHarPresent = useHarPresenceStore((state) => state.markPresent);
  const setToolHarGrpcDecode = useToolHarGrpcDecodeStore(
    (state) => state.setDecodedData,
  );
  const seenIdsRef = useRef<Set<string>>(new Set());

  const handleHarEvent = useCallback(
    async (event: ToolHarSSEEvent) => {
      if (event.applicationId !== targetId) {
        return;
      }

      markHarPresent(event.threadId, event.toolCallId);

      const eventKey = `${event.toolCallId}-${event.emittedAt}`;

      if (seenIdsRef.current.has(eventKey)) {
        return;
      }

      seenIdsRef.current.add(eventKey);

      if (seenIdsRef.current.size > 1000) {
        const entries = Array.from(seenIdsRef.current);

        seenIdsRef.current = new Set(entries.slice(-500));
      }

      const { harEntry } = event;
      const source = event.source;
      const requestId = `${source}-${event.toolCallId}-${Date.now()}`;
      const timestamp =
        new Date(harEntry.startedDateTime).getTime() || Date.now();

      const rawRequestText = harEntry.request.postData?.text;
      const rawResponseText = harEntry.response.content.text;
      const requestPostData = harEntry.request.postData;
      const requestBody = normalizeToolHarBodyForHistory({
        text: rawRequestText,
        encoding:
          requestPostData?.comment === HAR_BASE64_COMMENT
            ? 'base64'
            : undefined,
      });
      const responseBody = normalizeToolHarBodyForHistory({
        text: rawResponseText ?? undefined,
        encoding: harEntry.response.content.encoding,
      });
      const requestContentType =
        getContentType(harEntry.request.headers) ||
        harEntry.request.postData?.mimeType;
      const responseContentType =
        getContentType(harEntry.response.headers) ||
        harEntry.response.content.mimeType;
      const normalizedRequestHeaders = ensureContentTypeHeader({
        headers: harEntry.request.headers,
        contentType: requestContentType,
      });
      const normalizedResponseHeaders = ensureContentTypeHeader({
        headers: harEntry.response.headers,
        contentType: responseContentType,
      });
      const requestBodySize = normalizeHarBodySize({
        primarySize: harEntry.request.bodySize,
        fallbackText: requestBody,
      });
      const responseBodySize = normalizeHarBodySize({
        primarySize: harEntry.response.bodySize,
        secondarySize: harEntry.response.content.size,
        fallbackText: responseBody,
      });
      const requestHistoryBodyContentType = resolveHistoryBodyContentType({
        bodyText: requestBody,
        encoding:
          requestPostData?.comment === HAR_BASE64_COMMENT
            ? 'base64'
            : undefined,
        mimeType: requestContentType,
      });
      const responseHistoryBodyContentType = resolveHistoryBodyContentType({
        bodyText: responseBody,
        encoding: harEntry.response.content.encoding,
        mimeType: responseContentType,
      });

      const requestMetadata = {
        id: requestId,
        targetId,
        targetType,
        sequentialId: 0,
        method: harEntry.request.method,
        url: harEntry.request.url,
        host: getHostFromUrl(harEntry.request.url),
        path: getPathFromUrl(harEntry.request.url),
        statusCode: harEntry.response.status,
        statusMessage: harEntry.response.statusText,
        requestHeaders: harHeadersToRecord(normalizedRequestHeaders),
        requestRawHeaders: harHeadersToRawHeaders(normalizedRequestHeaders),
        responseHeaders: harHeadersToRecord(normalizedResponseHeaders),
        responseRawHeaders: harHeadersToRawHeaders(normalizedResponseHeaders),
        timing: JSON.stringify({
          wait: harEntry.timings.wait,
          receive: harEntry.timings.receive,
          total: harEntry.time,
        }),
        bodySize: requestBodySize,
        responseBodySize: responseBodySize,
        bodyContentType: requestHistoryBodyContentType,
        responseBodyContentType: responseHistoryBodyContentType,
        source,
        threadId: event.threadId,
        httpVersion: harEntry.request.httpVersion,
        responseHttpVersion: harEntry.response.httpVersion,
        timestamp,
      };

      try {
        if (!window.electronAPI?.history) {
          return;
        }

        await window.electronAPI.history.saveRequest({
          request: requestMetadata,
          body: {
            requestBody,
            responseBody,
          },
        });

        if (harEntry.webGrpcDecodedData) {
          setToolHarGrpcDecode({
            requestId,
            webGrpcDecodedData: harEntry.webGrpcDecodedData,
          });
        }

        useProxyStore.getState().incrementHistoryVersion();
      } catch (error) {
        console.error('Failed to save tool HAR to local DB:', error);
      }
    },
    [targetId, targetType, markHarPresent, setToolHarGrpcDecode],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribe = subscribe(TOOL_EXECUTION_HAR_SSE_EVENT, (event) => {
      try {
        handleHarEvent(event);
      } catch (error) {
        console.error('Error handling tool HAR SSE event:', error);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [subscribe, handleHarEvent, enabled]);
}
